/**
 * The scan screen: one spot owns the whole screen.
 *
 * Camera full-bleed underneath; where she is at the top; the verdict low on a scrim; actions
 * in the thumb rail on the right. After Place, the screen snaps to the next open spot and
 * carries "A1 placed · Undo" with it.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { ChevronDown, Undo2 } from "lucide-react";
import type { CalendarDate } from "@hitl/rotations-domain";
import { deriveDay, placeCommand, rejectCommand, undoCommand, whereCanItGo, writeOffCommand, type DaySetup } from "../state/session";
import { savePhoto } from "../state/photoStore";
import { WriteOffSheet } from "../reports/WriteOffSheet";
import type { EventLog, SyncState } from "../state/eventLog";
import { parsePotCode } from "../state/potCode";
import { createStabilizer } from "../state/stabilizer";
import { useQrScanner } from "./useQrScanner";
import { CameraNotice, DonePanel, RailButton, RailIcons, SwapLine, TopBar, VerdictPanel, WaitingPanel } from "./parts";
import { OverrideSheet, PotActionsSheet, RefuseSheet, RouteSheet, TypeNumberSheet, WhereCanItGoSheet, type OverrideKind } from "./sheets";
import { formatDay, WRITE_OFF_LABEL } from "./copy";
import type { Fixture } from "../demo/fixtures";

type SheetKind = "type" | "refuse" | "override" | "route" | "where" | "writeoff" | "more" | null;
interface Toast {
  readonly id: number;
  /** A pot number the text starts with, shown in the display face. */
  readonly pot?: string;
  readonly text: string;
  readonly undo?: { readonly eventIds: readonly string[]; readonly spotIndex: number };
}

const TOAST_MS = 6000;

/** The day-done sentence must agree with the sync badge above it. */
const DAY_DONE_SYNC: Record<SyncState, string> = {
  saved: "Everything is recorded and saved.",
  syncing: "Everything is saved on this phone and sending now.",
  offline: "Everything is saved on this phone. It sends when signal returns.",
  "storage-unavailable": "Recorded, but this phone couldn't store it. Keep the app open until you have signal.",
};

export function ScanScreen(props: {
  setup: DaySetup;
  log: EventLog;
  today: CalendarDate;
  routeName?: string;
  onOpenPlan?: () => void;
  onHome?: () => void;
  fixture?: Fixture;
}) {
  const { setup, log, today, fixture, onOpenPlan, onHome } = props;
  const events = useSyncExternalStore(log.subscribe, log.all);
  const syncState = useSyncExternalStore(log.subscribe, log.syncState);
  const pending = useSyncExternalStore(log.subscribe, log.pending);
  const day = useMemo(() => deriveDay(setup, events), [setup, events]);

  const [spotIndex, setSpotIndex] = useState(() => fixture?.spotIndex ?? day.nextOpenIndex ?? 0);
  const [scanned, setScanned] = useState<string | null>(fixture?.scanned ?? null);
  const [foreign, setForeign] = useState(false);
  const [sheet, setSheet] = useState<SheetKind>(fixture?.sheet ?? null);
  const [adHocPot, setAdHocPot] = useState<string | null>(null);
  const [scanAny, setScanAny] = useState(false);
  const [enterFrom, setEnterFrom] = useState<"above" | "below">("above");
  const [toast, setToast] = useState<Toast | null>(fixture?.toast ? { id: 1, text: fixture.toast, undo: { eventIds: [], spotIndex: 0 } } : null);
  const stabilizer = useRef(createStabilizer());

  const spot = day.spots[spotIndex] ?? day.spots[0]!;
  const dayDone = day.nextOpenIndex === null;
  const verdict = scanned ? day.assessor.assess(scanned, spot.slotId, today) : null;

  const handleCode = useCallback(
    (raw: string) => {
      const code = parsePotCode(raw);
      if (code.kind === "foreign") {
        setForeign(true);
        return;
      }
      setForeign(false);
      if (scanAny) {
        setAdHocPot(code.potId);
        setSheet("where");
        setScanAny(false);
        return;
      }
      setScanned(code.potId);
    },
    [scanAny],
  );

  const onDecode = useCallback(
    (text: string) => {
      if (sheet) return; // a sheet is open: don't change what's behind it
      const accepted = stabilizer.current.feed(text, performance.now());
      if (accepted) handleCode(accepted);
    },
    [sheet, handleCode],
  );
  // iOS only raises the keyboard for focus() inside the tap itself, never in a later render.
  const keyboardProxy = useRef<HTMLInputElement>(null);
  const openTypeSheet = () => {
    keyboardProxy.current?.focus({ preventScroll: true });
    setSheet("type");
  };

  // Sheets sit above the on-screen keyboard: iOS shrinks the visual viewport, not the layout.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () =>
      document.documentElement.style.setProperty("--kb", `${Math.max(0, window.innerHeight - vv.height - vv.offsetTop)}px`);
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  const scanner = useQrScanner(onDecode);
  const camera = fixture?.camera ? { ...scanner, status: fixture.camera } : scanner;

  // Toasts dismiss themselves.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast((cur) => (cur?.id === toast.id ? null : cur)), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  const goTo = (index: number) => {
    // The new spot enters from the side the thumb came from: next from above, previous from below.
    setEnterFrom(index >= spotIndex ? "above" : "below");
    stabilizer.current.reset();
    setScanned(null);
    setForeign(false);
    setSpotIndex(Math.max(0, Math.min(day.spots.length - 1, index)));
  };

  const place = (override?: string) => {
    if (!scanned) return;
    const created = log.append(placeCommand(day, spot, scanned, today, override));
    setToast({
      id: Date.now(),
      // The toast lands after the snap to the next spot, so it names the client it happened at.
      text: `${scanned} placed at ${spot.clientName}, ${spot.spotLabel}`,
      undo: { eventIds: created.map((e) => e.id), spotIndex: spot.index },
    });
    setSheet(null);
    // Snap to the next open spot after this one (wrapping), using the day as it is now.
    const next = day.spots.find((s) => s.index > spot.index && !s.done) ?? day.spots.find((s) => s.index !== spot.index && !s.done);
    goTo(next ? next.index : spot.index);
  };

  const undo = (t: Toast) => {
    if (!t.undo) return;
    log.append(undoCommand(t.undo.eventIds));
    setToast({ id: Date.now(), text: "Undone. Nothing was recorded." });
    goTo(t.undo.spotIndex);
  };

  const refuse = (reason: string) => {
    if (!scanned) return;
    const created = log.append(rejectCommand(spot, scanned, today, reason));
    setToast({ id: Date.now(), text: `Noted: ${spot.clientName} didn't want ${scanned}`, undo: { eventIds: created.map((e) => e.id), spotIndex: spot.index } });
    setSheet(null);
    stabilizer.current.reset();
    setScanned(null);
  };

  const nextSpot = day.spots[spotIndex + 1];
  const prevSpot = day.spots[spotIndex - 1];

  // The stop snap: the stage follows the thumb, the next spot peeks in from below, and past
  // the threshold it snaps. Buttons do the same thing for anyone who doesn't swipe.
  const SNAP_AT = 80;
  const drag = useRef<{ startY: number; pointerId: number; active: boolean } | null>(null);
  const [dragY, setDragY] = useState(fixture?.dragY ?? 0);
  const reducedMotion = useMemo(
    () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { startY: e.clientY, pointerId: e.pointerId, active: false };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    if (!d.active && Math.abs(dy) > 8) {
      d.active = true;
      e.currentTarget.setPointerCapture(d.pointerId);
    }
    if (!d.active || reducedMotion) return;
    // Resist where there's nowhere to go.
    const blocked = (dy > 0 && !nextSpot) || (dy < 0 && !prevSpot);
    setDragY(blocked ? dy * 0.25 : dy);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    setDragY(0);
    if (!d?.active) return;
    const dy = e.clientY - d.startY;
    // Down advances to the next spot, up goes back (Eddy, 2026-11-03).
    if (dy > SNAP_AT && nextSpot) goTo(spotIndex + 1);
    else if (dy < -SNAP_AT && prevSpot) goTo(spotIndex - 1);
  };
  const peek = Math.min(1, Math.max(0, dragY / 140));
  const isRed = verdict?.light === "red";
  const overrideKind: OverrideKind = verdict?.reasons.some((r) => r.code === "repeat-blocked")
    ? "repeat"
    : verdict?.reasons.some((r) => r.code === "pot-retired")
      ? "retired"
      : "unknown";

  return (
    <main className="scan">
      {/* Focused in the same tap that opens "Type number", so iOS raises the keyboard; the sheet's
          field then takes focus over from it and the keyboard stays up. */}
      <input ref={keyboardProxy} className="kb-proxy" aria-hidden tabIndex={-1} autoCapitalize="characters" autoComplete="off" />
      <video ref={camera.videoRef} className="scan__video" playsInline muted aria-hidden />
      <div className="scan__shade" aria-hidden />

      <TopBar spots={day.spots} current={spot} dayDone={dayDone} syncState={syncState} pending={pending} onOpenRoute={() => setSheet("route")} {...(onHome ? { onHome } : {})} />

      {camera.status === "live" && !scanned && !spot.done && !dayDone && <div className="frame" aria-hidden />}
      <CameraNotice status={camera.status} onStart={camera.start} onType={openTypeSheet} />

      {toast && (
        <div className="toast" role="status" key={toast.id}>
          <span>
            {toast.pot && <span className="inline-pot">{toast.pot}</span>}
            {toast.text}
          </span>
          {toast.undo && (
            <button className="toast__undo" onClick={() => undo(toast)}>
              <Undo2 aria-hidden size={18} strokeWidth={2.5} />
              Undo
            </button>
          )}
        </div>
      )}

      {!dayDone && (
      <section
        className={`stage stage--from-${enterFrom}${dragY !== 0 ? " is-dragging" : ""}`}
        key={spot.slotId}
        style={dragY !== 0 ? { transform: `translateY(${dragY}px)` } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-live="polite"
      >
        {nextSpot && (
          <div className="peek" style={{ opacity: peek }} aria-hidden>
            <span className="peek__client">{nextSpot.clientName}</span>
            <span className="peek__spot">{nextSpot.spotLabel}</span>
          </div>
        )}
        <div className="stage__info">
          {foreign && !scanned && <p className="foreign">That code isn't a Rotations sticker.</p>}
          {scanAny && <p className="foreign">Scan any pot to see where it can go.</p>}
          {verdict && scanned ? (
            <VerdictPanel
              potId={scanned}
              verdict={verdict}
              spot={spot}
              catalog={setup.catalog}
              history={day.history}
              today={today}
            />
          ) : spot.done ? (
            <DonePanel spot={spot} />
          ) : (
            <WaitingPanel spot={spot} />
          )}
          {!spot.done && <SwapLine spot={spot} />}
        </div>

        <nav className="rail" aria-label="Actions">
          {scanned && !isRed && <RailButton primary label={`Place ${scanned}`} icon={RailIcons.Check} onClick={() => place()} />}
          {scanned && isRed && (
            <RailButton
              primary
              label="Scan another"
              icon={RailIcons.ScanLine}
              onClick={() => {
                stabilizer.current.reset();
                setScanned(null);
              }}
            />
          )}
          {scanned && !isRed && <RailButton label="Client says no" icon={RailIcons.X} onClick={() => setSheet("refuse")} />}
          {scanned && isRed && (
            <RailButton label="Place anyway" icon={RailIcons.ArrowDownToLine} onClick={() => setSheet("override")} />
          )}
          {scanned ? (
            <RailButton label="More" icon={RailIcons.MoreHorizontal} onClick={() => setSheet("more")} />
          ) : (
            <RailButton label="Type number" icon={RailIcons.Keyboard} onClick={openTypeSheet} />
          )}
        </nav>
      </section>
      )}

      {!dayDone && (
      <footer className="next" style={dragY !== 0 ? { opacity: Math.max(0, 1 - Math.abs(dragY) / 60) } : undefined}>
        {/* The stage slides over the footer while dragging, so the footer steps aside. */}
        <button
          className="next__back"
          onClick={() => goTo(spotIndex - 1)}
          disabled={!prevSpot}
          aria-label={prevSpot ? `Back to ${prevSpot.clientName}, ${prevSpot.spotLabel}` : "Back"}
        >
          Back
        </button>
        <button className="next__main" onClick={() => goTo(spotIndex + 1)} disabled={!nextSpot}>
          {nextSpot ? (
            <>
              <ChevronDown aria-hidden size={20} className="next__icon" />
              {/* The client shortens first; the destination spot always stays readable. */}
              <span className="next__label">
                <span className="next__fixed">Next:{"\u00a0"}</span>
                <span className="next__client">{nextSpot.clientName}</span>
                <span className="next__fixed">{"\u202f· "}{nextSpot.spotLabel}</span>
              </span>
            </>
          ) : (
            <span className="next__label">Last spot of the day</span>
          )}
        </button>
      </footer>
      )}

      {dayDone && !sheet && (
        <div className="daydone" role="dialog" aria-labelledby="daydone-title">
          <h2 id="daydone-title">Day done</h2>
          <p>
            {day.spots.length} spots swapped on {formatDay(today)}. {DAY_DONE_SYNC[syncState]}
          </p>
          <p className="daydone__home">
            Coming home:{" "}
            {day.report.returnsToStock.length
              ? day.report.returnsToStock.map((p, i) => (
                  <span key={p}>
                    {i > 0 && ", "}
                    <span className="inline-pot">{p}</span>
                  </span>
                ))
              : "nothing"}
          </p>
          <button className="btn btn--ghost" onClick={() => setSheet("route")}>
            Review the route
          </button>
          {onOpenPlan && (
            <button className="btn btn--light" onClick={onOpenPlan}>
              Plan the next days
            </button>
          )}
        </div>
      )}

      {sheet === "type" && <TypeNumberSheet onClose={() => setSheet(null)} onSubmit={(v) => { setSheet(null); handleCode(v); }} />}
      {sheet === "refuse" && scanned && (
        <RefuseSheet potId={scanned} clientName={spot.clientName} onSubmit={refuse} onClose={() => setSheet(null)} />
      )}
      {sheet === "more" && scanned && (
        <PotActionsSheet
          potId={scanned}
          canWriteOff={Boolean(setup.catalog.pot(scanned)) && !day.history.writtenOff(scanned)}
          onType={openTypeSheet}
          onWriteOff={() => setSheet("writeoff")}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === "writeoff" && scanned && (
        <WriteOffSheet
          potId={scanned}
          onClose={() => setSheet(null)}
          onSubmit={(reason, note, photo) =>
            void (async () => {
              const photoId = photo ? await savePhoto(photo) : null;
              const created = log.append(writeOffCommand(scanned, today, reason, { note, ...(photoId ? { photoId } : {}) }));
              setToast({ id: Date.now(), pot: scanned, text: ` written off: ${WRITE_OFF_LABEL[reason].toLowerCase()}`, undo: { eventIds: created.map((e) => e.id), spotIndex: spot.index } });
              setSheet(null);
              stabilizer.current.reset();
              setScanned(null);
            })()
          }
        />
      )}
      {sheet === "override" && scanned && (
        <OverrideSheet potId={scanned} clientName={spot.clientName} kind={overrideKind} onSubmit={(r) => place(r)} onClose={() => setSheet(null)} />
      )}
      {sheet === "route" && (
        <RouteSheet
          spots={day.spots}
          currentIndex={spotIndex}
          today={today}
          onClose={() => setSheet(null)}
          onPick={(i) => {
            setSheet(null);
            goTo(i);
          }}
          onScanAny={() => {
            setSheet(null);
            stabilizer.current.reset();
            setScanned(null);
            setScanAny(true);
          }}
          {...(onOpenPlan ? { onOpenPlan: () => { setSheet(null); onOpenPlan(); } } : {})}
          onResetDemo={() => {
            log.resetLocal();
            setSheet(null);
            goTo(0);
          }}
        />
      )}
      {sheet === "where" && adHocPot && (
        <WhereCanItGoSheet
          potId={adHocPot}
          standing={(() => {
            const at = day.history.whereIs(adHocPot);
            if (!setup.catalog.pot(adHocPot)) return `${adHocPot} isn't one of your pots.`;
            if (!at) return `${adHocPot} is in stock (or in the car).`;
            const client = setup.catalog.client(at.clientId)?.name ?? at.clientId;
            return `Records say ${adHocPot} is at ${client}, ${setup.catalog.slot(at.slotId).label}, since ${formatDay(at.placedOn)}.`;
          })()}
          options={whereCanItGo(day, adHocPot, today)}
          onClose={() => setSheet(null)}
          onPick={(i) => {
            setSheet(null);
            goTo(i);
            setScanned(adHocPot);
          }}
        />
      )}
    </main>
  );
}
